import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { api, fileUrl, formatApiError } from "../../lib/api";
import { AdminLayout } from "./AdminLayout";
import { PLACEMENTS } from "../../data/site";

export default function AdminPortfolio() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ image: "", artist_slug: "", style_slug: "", placement: "", orientation: "portrait" });
  const [uploading, setUploading] = useState(false);

  const { data: items = [] } = useQuery({ queryKey: ["admin-portfolio"], queryFn: async () => (await api.get("/admin/portfolio")).data });
  const { data: artists = [] } = useQuery({ queryKey: ["admin-artists"], queryFn: async () => (await api.get("/admin/artists")).data });
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });

  const artistMap = Object.fromEntries(artists.map((a) => [a.slug, a.name]));
  const styleMap = Object.fromEntries(styles.map((s) => [s.slug, s.name]));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const add = async () => {
    if (!form.image) { toast.error("Upload an image first"); return; }
    try {
      await api.post("/admin/portfolio", form);
      toast.success("Added to gallery");
      qc.invalidateQueries({ queryKey: ["admin-portfolio"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      setForm({ image: "", artist_slug: "", style_slug: "", placement: "", orientation: "portrait" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the portfolio? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/portfolio/${id}`);
      toast.success("Removed");
      qc.invalidateQueries({ queryKey: ["admin-portfolio"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const update = async (id, patch) => {
    try {
      await api.put(`/admin/portfolio/${id}`, patch);
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["admin-portfolio"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Portfolio">
      <div className="border border-ash bg-charcoal p-6 mb-8" data-testid="portfolio-uploader">
        <p className="eyebrow mb-5">Add a piece</p>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-end">
          <label className="lg:col-span-1 flex flex-col items-center justify-center gap-2 border border-dashed border-ash aspect-square cursor-pointer hover:border-ember transition-colors overflow-hidden">
            {form.image ? <img src={fileUrl(form.image)} alt="" className="w-full h-full object-cover" /> : <><Upload size={22} className="text-[#828290]" /><span className="text-[#828290] font-mono text-[0.6rem] uppercase">{uploading ? "…" : "Upload"}</span></>}
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="portfolio-upload-input" />
          </label>
          <Sel label="Artist" v={form.artist_slug} on={(v) => setForm({ ...form, artist_slug: v })} opts={artists.map((a) => [a.slug, a.name])} tid="portfolio-artist" />
          <Sel label="Style" v={form.style_slug} on={(v) => setForm({ ...form, style_slug: v })} opts={styles.map((s) => [s.slug, s.name])} tid="portfolio-style" />
          <Sel label="Placement" v={form.placement} on={(v) => setForm({ ...form, placement: v })} opts={PLACEMENTS.map((p) => [p, p])} tid="portfolio-placement" />
          <button onClick={add} data-testid="portfolio-add" className="bg-ember text-obsidian font-mono text-xs uppercase tracking-widest font-semibold py-3.5 hover:bg-white transition-colors">Add</button>
        </div>
      </div>

      <div className="columns-2 lg:columns-4 gap-4 [column-fill:_balance]">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-pf-${p.id}`} className="mb-4 break-inside-avoid border border-ash bg-charcoal group relative">
            <img src={fileUrl(p.image)} alt="" className="w-full object-cover" />
            <div className="p-3 space-y-2">
              <Sel label="Artist" v={p.artist_slug || ""} on={(v) => update(p.id, { artist_slug: v })} opts={artists.map((a) => [a.slug, a.name])} tid={`pf-edit-artist-${p.id}`} />
              <Sel label="Style" v={p.style_slug || ""} on={(v) => update(p.id, { style_slug: v })} opts={styles.map((s) => [s.slug, s.name])} tid={`pf-edit-style-${p.id}`} />
              <Sel label="Placement" v={p.placement || ""} on={(v) => update(p.id, { placement: v })} opts={PLACEMENTS.map((pl) => [pl, pl])} tid={`pf-edit-placement-${p.id}`} />
            </div>
            <button onClick={() => remove(p.id)} className="absolute top-2 right-2 bg-red-600 hover:bg-red-500 text-white rounded-full p-2 shadow-lg transition-colors z-10" data-testid={`pf-remove-${p.id}`} aria-label="Remove photo"><X size={18} /></button>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

function Sel({ label, v, on, opts, tid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <select data-testid={tid} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-obsidian border border-ash text-white px-3 py-3 text-sm outline-none focus:border-ember">
        <option value="">—</option>
        {opts.map(([val, name]) => <option key={val} value={val}>{name}</option>)}
      </select>
    </label>
  );
}
